import { FileDownload, ResultSet } from '@shared/http';

import { BidAnalysis, BidAnalysisBase } from '@/features/bid-analysis/types/bid-analysis';
import {
  BID_ANALYSIS_STATUS_CLOSED,
  BID_ANALYSIS_STATUS_OPEN,
  BidAnalysisStatusType
} from '@/features/bid-analysis/types/bid-analysis-status-type';
import { GetBidAnalysesRequest } from '@/features/bid-analysis/types/get-bid-analyses-request';
import { useFileDownload } from '@/hooks/use-file-download';
import { artiaProtectedApi } from '@/lib/api-client';
import { deleteWithValidation, downloadFile, postWithValidation, putWithValidation, RequestOptions } from '@/lib/api-helpers';

type CreateBidAnalysisRequest = {
  name: string;
  states: { state: string; benefit: string }[],
  pool?: string;
  marketBasketId: number;
  drugId: number;
  year: string;
  bidDueOn: string;
  reviewOn: string;
  contractTermStartsOn: string;
  contractTermEndsOn: string;
  periods: string[]
};

type UpdateBidAnalysisRequest = {
  name: string;
  bidDueOn: string;
  reviewOn: string;
  contractTermStartsOn: string;
  contractTermEndsOn: string;
  notes: string;
};

type CreateNewClinicalsProductRequest = {
  productName: string;
  classificationId: number;
  stateCode?: string;
};

const useBidAnalysisService = () => {
  const getBidAnalysis = (id: number): Promise<BidAnalysis> => {
    return artiaProtectedApi.get(`bid-analyses/${id}`);
  };

  const getBidAnalyses = (request?: GetBidAnalysesRequest): Promise<ResultSet<BidAnalysisBase>> => {
    const statusMap = {
      [BID_ANALYSIS_STATUS_OPEN]: 'open',
      [BID_ANALYSIS_STATUS_CLOSED]: 'closed',
      default: 'open'
    };

    const status = statusMap[request?.status ?? 'default'];
    return artiaProtectedApi.get('bid-analyses', { params: { status } });
  };

  const downloadAttachment = useFileDownload();

  const createBidAnalysis = (request: CreateBidAnalysisRequest): Promise<any> => {
    const options: RequestOptions<any> = {
      data: request
    };

    return postWithValidation('bid-analyses', options);
  };

  const deleteBidAnalysis = (id: number): Promise<void> => {
    return deleteWithValidation(`bid-analyses/${id}`);
  };

  const updateBidAnalysis = (bidAnalysisId: number, request: UpdateBidAnalysisRequest): Promise<any> => {
    if (request.bidDueOn === '') {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
      request.bidDueOn = null;
    }

    if (request.reviewOn === '') {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
      request.reviewOn = null;
    }

    const options: RequestOptions<any> = {
      data: request
    };

    return putWithValidation(`bid-analyses/${bidAnalysisId}`, options);
  };

  const uploadAttachment = (file: File, bidAnalysisId: number): Promise<any> => {
    const data = new FormData();

    data.append('Upload', file);

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation(`bid-analyses/${bidAnalysisId}/attachments`, options);
  };

  const refreshMarketDetails = (bidAnalysisId: number): Promise<any> => {
    return postWithValidation(`bid-analyses/${bidAnalysisId}/refresh`);
  };

  const exportBidRecommendations = (bidAnalysisId: number): Promise<FileDownload> => {
    return downloadFile('POST', `bid-analyses/${bidAnalysisId}/recommendations/export`);
  };

  const updateBidAnalysisStatus = (bidAnalysisId: number, status: BidAnalysisStatusType ) => {
    return putWithValidation(`bid-analyses/${bidAnalysisId}/status`, { data: { status } });
  };

  const reconcileDrug = (bidAnalysisId: number, drugId: number, clinicalsProductId: number) => {
    return postWithValidation(`bid-analyses/${bidAnalysisId}/drugs/${drugId}/reconcile`, {
      data: clinicalsProductId
    });
  };

  const reconcileDrugWithNewClinicalsProduct = (bidAnalysisId: number, drugId: number, request: CreateNewClinicalsProductRequest) => {
    return postWithValidation(`bid-analyses/${bidAnalysisId}/drugs/${drugId}/reconcile-new`, {
      data: request
    });
  };

  const reconcileNdc = (bidAnalysisId: number, ndc: string, clinicalsProductId: number, stateCode: string) => {
    return postWithValidation(`bid-analyses/${bidAnalysisId}/packagings/${ndc}/reconcile`, {
      data: { clinicalsProductId, stateCode }
    });
  };

  const reconcileNdcWithNewClinicalsProduct = (bidAnalysisId: number, ndc: string, request: CreateNewClinicalsProductRequest) => {
    return postWithValidation(`bid-analyses/${bidAnalysisId}/packagings/${ndc}/reconcile-new`, {
      data: request
    });
  };

  return {
    getBidAnalysis,
    getBidAnalyses,
    deleteBidAnalysis,
    downloadAttachment,
    createBidAnalysis,
    updateBidAnalysis,
    uploadAttachment,
    refreshMarketDetails,
    exportBidRecommendations,
    updateBidAnalysisStatus,
    reconcileDrug,
    reconcileDrugWithNewClinicalsProduct,
    reconcileNdc,
    reconcileNdcWithNewClinicalsProduct
  };
};

export { CreateBidAnalysisRequest, UpdateBidAnalysisRequest, useBidAnalysisService };
